import { useCallback, useEffect, useRef, useState } from 'react';
import { sleep } from '@chainflip/utils/async';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { DashboardModalWithCloseButton } from '@/shared/components/dashboards/Modal';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { Divider } from '@/shared/components/Divider';
import Checkbox from '@/shared/components/flip-ui-kit/Checkbox';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import TokenInputAmount from '@/shared/components/input/TokenInputAmount';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLoanRiskType } from '@/shared/hooks/useLoanRiskType';
import { type Loan } from '@/shared/hooks/useLpLoanAccount';
import { useOraclePrice } from '@/shared/hooks/useOraclePrice';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import PiggybankIcon from '@/shared/icons/flip-ui-kit/small/Piggybank';
import { apyToText } from '@/shared/utils/boost';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap } from '@/shared/utils/env';
import { LENDING_POOLS_QUERY } from '@/shared/utils/lending';
import { formatUsdValue, percentToPips } from '@/shared/utils/numbers';
import { formatBps } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import useLoanAccount from '../../../hooks/useLoanAccount';
import { useExtrinsicSDK, useStateChainAccount } from '../../../hooks/useStateChainAccount';
import SuccessIllustration from '../../../public/success-illustration.svg';

interface RepayModalProps {
  open: boolean;
  onClose: () => void;
  afterLeave: () => void;
  initialLoan: Loan;
  currentLoan: Loan | undefined;
}

const Success = ({
  asset,
  amount,
  children,
  amountUsd,
  isLoanSettled,
}: {
  asset: ChainflipAsset;
  amount: TokenAmount;
  children: React.ReactNode;
  amountUsd: number;
  isLoanSettled: boolean;
}) => (
  <div className="flex flex-col gap-6 rounded-md">
    <div className="my-auto flex w-full flex-col items-center justify-center">
      <div className="mb-[-74px]">
        <SuccessIllustration />
      </div>
      <div className="flex flex-col items-center gap-y-2 font-aeonikMedium">
        <span className="text-24 text-cf-white">
          {isLoanSettled
            ? `${chainflipAssetMap[asset].symbol} Debt fully repaid`
            : 'Repayment Successful'}
        </span>
        <span className="flex items-center gap-x-1">
          You paid {amount.toFormatted()}
          <TokenWithChainByAsset asset={asset} />
          {chainflipAssetMap[asset].symbol}
          <span className="text-cf-light-2">({formatUsdValue(amountUsd)})</span>
        </span>
      </div>
    </div>
    {children}
  </div>
);

const PositionInfoBox = ({
  token,
  principalAmount,
  principalAmountUsd,
  projectedPrincipalAmount,
  projectedPrincipalAmountUsd,
  loanToValueBps,
  projectedLoanToValuePercent,
  borrowCapacityUsd,
  projectedBorrowCapacityUsd,
}: {
  token: ChainflipToken;
  principalAmount: TokenAmount;
  principalAmountUsd: number;
  projectedPrincipalAmount?: TokenAmount;
  projectedPrincipalAmountUsd?: number;
  loanToValueBps: number;
  projectedLoanToValuePercent?: number;
  borrowCapacityUsd: number;
  projectedBorrowCapacityUsd?: number;
}) => {
  const { riskType, riskColor } = useLoanRiskType(
    isNotNullish(projectedLoanToValuePercent)
      ? percentToPips(projectedLoanToValuePercent)
      : loanToValueBps,
  );

  return (
    <div className="flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium shadow-grayPop3">
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
          Outstanding {token.symbol} Debt
          <QuestionMarkTooltip content="Your outstanding debt for this loan, including interest accrued, before and after repayment." />
        </div>
        <div className="flex flex-col gap-y-0.5 text-20">
          <div className="flex items-center gap-x-1">
            <span
              className={classNames(
                isNotNullish(projectedPrincipalAmount) ? 'text-cf-light-2' : 'text-cf-white',
              )}
            >
              {principalAmount.toFormatted()}
            </span>
            {isNullish(projectedPrincipalAmount) && (
              <>
                <TokenLogo size="small" token={token} />
                {token.symbol}
              </>
            )}
            {isNotNullish(projectedPrincipalAmount) && (
              <>
                <ArrowIcon className="text-cf-light-2" />
                <span className="text-cf-white">{projectedPrincipalAmount.toFormatted()}</span>
                <TokenLogo size="small" token={token} />
                {token.symbol}
              </>
            )}
          </div>
          <div className="flex items-center gap-x-2">
            <span
              className={classNames(isNotNullish(projectedPrincipalAmount) && 'text-cf-light-2')}
            >
              {formatUsdValue(principalAmountUsd)}
            </span>
            {isNotNullish(projectedPrincipalAmountUsd) && (
              <>
                <ArrowIcon className="text-cf-light-2" />
                <span className="text-cf-white">{formatUsdValue(projectedPrincipalAmountUsd)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Divider className="h-px" />
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
          Loan-to-Value (LTV)
          <QuestionMarkTooltip content="The ratio between your borrowed amount and the value of your collateral, before and after repayment." />
        </div>
        <div className="flex items-center gap-x-1 text-20">
          <span
            className={classNames(
              isNotNullish(projectedLoanToValuePercent) ? 'text-cf-light-2' : 'text-cf-white',
            )}
          >
            {formatBps(loanToValueBps)}
          </span>
          {isNotNullish(projectedLoanToValuePercent) && (
            <>
              <ArrowIcon className="text-cf-light-2" />
              <span
                className={classNames(
                  riskType === 'Conservative' ? 'text-cf-green-1' : 'text-cf-white',
                )}
              >
                {apyToText(projectedLoanToValuePercent)}
              </span>
            </>
          )}
          <Pill color={riskColor} text={riskType} />
        </div>
      </div>
      <Divider className="h-px" />
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
          Borrow power
          <QuestionMarkTooltip content="The maximum amount you can borrow based on the value of your supplied assets and outstanding loans, before and after repayment." />
        </div>
        <div className="flex items-center gap-x-1 text-20">
          <span
            className={classNames(
              isNotNullish(projectedBorrowCapacityUsd) ? 'text-cf-light-2' : 'text-cf-white',
            )}
          >
            {formatUsdValue(borrowCapacityUsd)}
          </span>
          {isNotNullish(projectedBorrowCapacityUsd) && (
            <>
              <ArrowIcon className="text-cf-light-2" />
              <span className="text-cf-white">{formatUsdValue(projectedBorrowCapacityUsd)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const RepayModal = ({
  open,
  onClose,
  afterLeave,
  initialLoan,
  currentLoan,
}: RepayModalProps) => {
  const { freeBalances, refreshAccountInfo, isLoading: isBalanceLoading } = useStateChainAccount();
  const {
    asset,
    principalAmount,
    principalAmountUsd,
    id: loanId,
  } = currentLoan ?? { ...initialLoan, principalAmount: TokenAmount.ZERO, principalAmountUsd: 0 };

  const {
    loanAccount: { isLiquidating, loanToValueBps, totalBorrowAmountUsd, totalCollateralBalanceUsd },
  } = useLoanAccount();
  const { targetLtvThresholdBps, minLoanUpdateAmountUsd, minLoanRequestAmountUsd } =
    useLendingConfig();
  const price = useOraclePrice(asset);
  const focusRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string>('');
  const [emptyAmountError, setEmptyAmountError] = useState<string>();

  const balance = freeBalances?.[chainflipAssetMap[asset].chainflipId] ?? TokenAmount.ZERO;

  const canRepayFully = principalAmount.lte(balance);

  const [fullRepaymentConsent, setFullRepaymentConsent] = useState(false);
  const [userChangedConsent, setUserChangedConsent] = useState(false);
  const [systemRevision, setSystemRevision] = useState(0);
  const [amount, setAmount] = useState<TokenAmount>();
  const [prevValues, setPrevValues] = useState<{
    amount: TokenAmount | undefined;
    principalAmount: TokenAmount;
    principalAmountUsd: number;
    loanToValueBps: number;
    borrowCapacityUsd: number;
  }>();
  const queryClient = useQueryClient();
  const { toast, sendExtrinsic } = useExtrinsicSDK('makeRepayment');

  const amountUsd = (amount ?? TokenAmount.ZERO).toUsdNumber(price);
  const borrowCapacityUsd = Math.max(
    (totalCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 - totalBorrowAmountUsd,
    0,
  );

  const projectedPrincipalAmount = principalAmount.sub(amount ?? TokenAmount.ZERO);
  const projectedPrincipalAmountUsd = projectedPrincipalAmount.toUsdNumber(price);
  const outstandingBorrowedAmountUsd = totalBorrowAmountUsd - amountUsd;

  const projectedLoanToValuePercent =
    totalCollateralBalanceUsd > 0
      ? Math.floor((outstandingBorrowedAmountUsd / totalCollateralBalanceUsd) * 100 * 100) / 100 // truncate to 2 decimal places without rounding
      : undefined;
  const projectedBorrowCapacityUsd = Math.max(
    (totalCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 -
      outstandingBorrowedAmountUsd,
    0,
  );

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async () => {
      setPrevValues({
        amount,
        principalAmount,
        principalAmountUsd,
        loanToValueBps,
        borrowCapacityUsd,
      });

      await sendExtrinsic(
        BigInt(loanId),
        fullRepaymentConsent ? 'Full' : { Exact: amount?.toString() ?? '' },
      );

      return amount!;
    },
    async onSuccess(submittedAmount) {
      await sleep(500);
      await Promise.all(
        [LENDING_POOLS_QUERY, 'cf_loan_accounts'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      await refreshAccountInfo();
      toast.success(
        `Loan was repaid with ${submittedAmount.toFormatted()} ${chainflipAssetMap[asset].symbol}`,
      );
    },
    onError(err) {
      setPrevValues(undefined);
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  const ctaClickHandler = () => {
    if (!amount || amount.lte(0)) {
      setEmptyAmountError('Please enter a valid amount');
      return;
    }
    if (!fullRepaymentConsent && amountUsd < minLoanUpdateAmountUsd) {
      setError(
        `Amount should be greater than the minimum loan update amount: ${formatUsdValue(minLoanUpdateAmountUsd)}`,
      );
      return;
    }
    if (
      !fullRepaymentConsent &&
      projectedPrincipalAmount.gt(0) &&
      projectedPrincipalAmountUsd < minLoanRequestAmountUsd
    ) {
      setError(
        `Outstanding loan amount should be greater than the minimum loan request amount: ${formatUsdValue(minLoanRequestAmountUsd)}`,
      );
      return;
    }
    if (amount.gt(principalAmount)) {
      setError('Amount exceeds outstanding debt');
      return;
    }
    mutate();
  };

  const handleTokenInput = useCallback((value: TokenAmount | undefined) => {
    setAmount(value);
    if (value?.gt(0)) setEmptyAmountError(undefined);
  }, []);

  const bumpSystemRevision = () => setSystemRevision((v) => v + 1);

  const handleFullRepaymentConsent = (value: boolean) => {
    setFullRepaymentConsent(value);
    if (!value) {
      setAmount(undefined);
      bumpSystemRevision();
    }
    if (!userChangedConsent) setUserChangedConsent(true);
  };

  const isProjectedValueDisabled =
    !amount ||
    amount.eq(0) ||
    amount.gt(principalAmount) ||
    (isPending && prevValues && principalAmount.lt(prevValues.principalAmount)) || // if principalAmount has changed due to repayment do not show further projection
    (!isPending && amount.gt(balance));

  useEffect(() => {
    if (!userChangedConsent && !amount && !fullRepaymentConsent && canRepayFully) {
      setFullRepaymentConsent(true);
    }
  }, [userChangedConsent, fullRepaymentConsent, amount, canRepayFully]);

  useEffect(() => {
    if (fullRepaymentConsent) {
      setAmount(principalAmount);
      bumpSystemRevision();
      setEmptyAmountError(undefined);
    }
  }, [fullRepaymentConsent, principalAmount]);

  return (
    <DashboardModalWithCloseButton
      initialFocus={focusRef}
      open={open}
      onClose={onClose}
      afterLeave={() => {
        afterLeave();
        setFullRepaymentConsent(false);
        setAmount(undefined);
        setUserChangedConsent(false);
        setSystemRevision(0);
        setPrevValues(undefined);
        setError('');
        setEmptyAmountError(undefined);
        resetMutation();
      }}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="repay-success" {...defaultAnimationProps}>
            <Success
              asset={asset}
              amount={prevValues?.amount ?? TokenAmount.ZERO}
              amountUsd={(prevValues?.amount ?? TokenAmount.ZERO).toUsdNumber(price)}
              isLoanSettled={principalAmount.eq(0)}
            >
              <PositionInfoBox
                token={chainflipAssetMap[asset]}
                principalAmount={prevValues?.principalAmount ?? TokenAmount.ZERO}
                principalAmountUsd={prevValues?.principalAmountUsd ?? 0}
                projectedPrincipalAmount={principalAmount}
                projectedPrincipalAmountUsd={principalAmountUsd}
                loanToValueBps={prevValues?.loanToValueBps ?? 0}
                projectedLoanToValuePercent={loanToValueBps / 100}
                borrowCapacityUsd={prevValues?.borrowCapacityUsd ?? 0}
                projectedBorrowCapacityUsd={borrowCapacityUsd}
              />
              <div className="flex flex-col gap-y-2">
                {principalAmount.gt(0) && (
                  <ReadonlyButton
                    className="w-full"
                    type="secondary-standard"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFullRepaymentConsent(canRepayFully);
                      setAmount(canRepayFully ? principalAmount : undefined);
                      setUserChangedConsent(false);
                      setSystemRevision(0);
                      setPrevValues(undefined);
                      setError('');
                      setEmptyAmountError(undefined);
                      resetMutation();
                    }}
                  >
                    Make another payment
                  </ReadonlyButton>
                )}
                <ReadonlyButton
                  className="w-full"
                  type="primary-standard"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  View Position
                </ReadonlyButton>
              </div>
            </Success>
          </motion.div>
        ) : (
          <motion.div key="repay-form" {...defaultAnimationProps}>
            <div className="flex max-w-[437px] flex-col gap-y-6">
              <div className="flex flex-col gap-y-3 font-aeonikMedium">
                <div className="text-20 text-cf-white">Repay Loan</div>
                <p className="align-middle text-14 text-cf-light-2">
                  Choose how much of your debt you&apos;d like to pay from{' '}
                  <span className="inline-flex items-center gap-x-1 align-middle">
                    <PiggybankIcon />
                    <span className="text-cf-white">Balance</span>
                  </span>
                </p>
              </div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="full-repayment-checkbox"
                className={classNames(
                  'flex items-center justify-between rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-3',
                  !isPending && (fullRepaymentConsent || canRepayFully) && 'hover:cursor-pointer',
                )}
              >
                <div className="flex items-center gap-x-4">
                  <Checkbox
                    id="full-repayment-checkbox"
                    setChecked={(value) => handleFullRepaymentConsent(!!value)}
                    checked={fullRepaymentConsent}
                    disabled={isPending || (!fullRepaymentConsent && !canRepayFully)}
                  />
                  <div
                    className={classNames(
                      'flex items-center gap-x-1',
                      !fullRepaymentConsent && !canRepayFully && 'text-cf-light-1',
                    )}
                  >
                    Repay full amount
                    <QuestionMarkTooltip content="Repays and closes your loan position in full. Available when your account balance covers the outstanding debt." />
                  </div>
                </div>
                <span
                  className={classNames(
                    'text-cf-green-1',
                    !fullRepaymentConsent && !canRepayFully && 'opacity-50',
                  )}
                >
                  Recommended
                </span>
              </label>
              <TokenInputAmount
                key={systemRevision}
                label="Amount"
                token={chainflipAssetMap[asset]}
                balance={balance}
                value={amount}
                onChange={handleTokenInput}
                error={error || emptyAmountError}
                showError={!isPending}
                disabled={!chainflipAssetMap[asset] || fullRepaymentConsent || isPending}
                disableMaxButton={canRepayFully}
                useBalanceAsMaxButton
                focusOnMount
                isBalanceLoading={isBalanceLoading}
                setError={setError}
                balanceLabel="Account Balance"
              />
              <Divider className="h-px" />
              <PositionInfoBox
                token={chainflipAssetMap[asset]}
                principalAmount={principalAmount}
                principalAmountUsd={principalAmountUsd}
                projectedPrincipalAmount={
                  isProjectedValueDisabled ? undefined : projectedPrincipalAmount
                }
                projectedPrincipalAmountUsd={
                  isProjectedValueDisabled ? undefined : projectedPrincipalAmountUsd
                }
                loanToValueBps={loanToValueBps}
                projectedLoanToValuePercent={
                  isProjectedValueDisabled ? undefined : projectedLoanToValuePercent
                }
                borrowCapacityUsd={borrowCapacityUsd}
                projectedBorrowCapacityUsd={
                  isProjectedValueDisabled ? undefined : projectedBorrowCapacityUsd
                }
              />
              <ReadonlyButton
                fullWidth
                onClick={ctaClickHandler}
                loading={isPending && 'iconOnly'}
                disabled={Boolean(emptyAmountError) || Boolean(error) || isPending || isLiquidating}
              >
                Confirm Repayment
              </ReadonlyButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModalWithCloseButton>
  );
};
